import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import FormInput from '../components/FormInput'
import AgreementItem from '../components/AgreementItem'
import LegalDocViewer, { LegalDocType } from '../components/LegalDocViewer'
import ProfileImageUpload from '../components/ProfileImageUpload'
import { toastUtil } from '../utils/toast'
import { validatePassword } from '../utils/passwordValidation'
import { authApi, ApiErrorResponse } from '../api'
import { debugLogger } from '../utils/debugLogger'
import { useAuthStore } from '../stores/authStore'
import { userApi } from '../api/user'
import BoldCheckbox from '../components/BoldCheckbox'
import FormErrorMessage from '../components/FormErrorMessage'
import {
  verifyPhoneForRegister,
  normalizeRegisterPhoneInput,
} from '../utils/phoneVerification'
import { SocialProvider } from '../constants/social'
import {
  isRegisterDebugEmailWithSocial,
  isRegisterDebugSkipPhoneVerification,
} from '../utils/registerDebugFeatures'
import { isApp } from '../utils/platform'

const Register: React.FC = () => {
  const navigate = useNavigate()
  const {
    setToken,
    setUserId,
    setProfileImage: setStoreProfileImage,
    getPostLoginPath,
  } = useAuthStore()
  const [searchParams] = useSearchParams()
  const [legalViewer, setLegalViewer] = useState<LegalDocType | null>(null)
  const [email, setEmail] = useState('')
  const [emailVerified, setEmailVerified] = useState(false)
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [isRegistering, setIsRegistering] = useState(false)
  const [id, setId] = useState('')
  const [idVerified, setIdVerified] = useState(false)
  const [isCheckingId, setIsCheckingId] = useState(false)
  const [idError, setIdError] = useState('')
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [passwordConfirmError, setPasswordConfirmError] = useState('')
  const [nickname, setNickname] = useState('')
  const [nicknameError, setNicknameError] = useState('')
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null
  )
  const [profileImageError, setProfileImageError] = useState('')
  const [agreeAll, setAgreeAll] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [agreeTermsError, setAgreeTermsError] = useState('')
  const [agreePrivacyError, setAgreePrivacyError] = useState('')
  const [socialProvider, setSocialProvider] = useState<string | null>(null)
  const [socialProviderId, setSocialProviderId] = useState<string | null>(null)

  const useEmailPasswordWithSocial = isRegisterDebugEmailWithSocial()
  const skipPhoneVerification = isRegisterDebugSkipPhoneVerification()
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')

  // 쿼리 파라미터에서 소셜 로그인 정보 읽기 및 디버그 기본값 설정
  useEffect(() => {
    const encodedData = searchParams.get('data')

    if (encodedData) {
      try {
        const decodedData = atob(encodedData)
        const socialData = JSON.parse(decodedData)

        if (socialData.provider && socialData.provider_id) {
          setSocialProvider(socialData.provider)
          setSocialProviderId(socialData.provider_id)
        }
      } catch (error) {
        debugLogger.error('[Register] 소셜 로그인 정보 디코딩 실패:', error)
      }
    }

    // development에서만 휴대폰·비밀번호·약관 등 기본값
    if (isRegisterDebugEmailWithSocial()) {
      setPhone('01012345678')
      setPassword('Abcd1234!')
      setPasswordConfirm('Abcd1234!')
      setNickname('test')
      setAgreeAll(true)
      setAgreeTerms(true)
      setAgreePrivacy(true)
    }
  }, [searchParams])

  const handleEmailCheck = async () => {
    if (!email) {
      setEmailError('이메일을 입력해주세요')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('올바른 이메일 형식이 아닙니다')
      return
    }

    if (emailVerified) {
      toastUtil.info('이미 확인 완료된 이메일입니다')
      return
    }

    if (isCheckingEmail) {
      toastUtil.info('이미 확인 중입니다')
      return
    }

    setIsCheckingEmail(true)
    try {
      const response = await authApi.checkEmail({ email })
      if (response.available) {
        setEmailVerified(true)
        setEmailError('')
        toastUtil.success('사용 가능한 이메일입니다')
      } else {
        setEmailVerified(false)
        setEmailError(response.message || '사용할 수 없는 이메일입니다')
      }
    } catch (error: any) {
      setEmailVerified(false)
      const apiError = error.response?.data as ApiErrorResponse
      if (apiError?.errors?.email) {
        setEmailError(apiError.errors.email[0])
      } else {
        setEmailError(apiError?.message || '이메일 확인 중 오류가 발생했습니다')
      }
    } finally {
      setIsCheckingEmail(false)
    }
  }

  const handleRegister = async () => {
    // 폼 검증 (본인인증은 이미 완료되었으므로 phone 검증 제외)
    const isSocialOnlyRegister =
      !!(socialProvider && socialProviderId) && !useEmailPasswordWithSocial

    if (!isSocialOnlyRegister) {
      if (!email) {
        setEmailError('이메일을 입력해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailError('올바른 이메일 형식이 아닙니다')
        toastUtil.error('입력 값을 확인해주세요')
        return
      } else if (!emailVerified) {
        setEmailError('이메일 중복 확인을 해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return
      }
    }

    if (!id) {
      setIdError('아이디를 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (id.length < 3 || id.length > 20) {
      setIdError('아이디는 3~20자로 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (!/^[a-zA-Z0-9._]+$/.test(id)) {
      setIdError('아이디는 영문, 숫자, 언더스코어(_)·마침표(.)만 사용 가능합니다')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (/[._]$/.test(id)) {
      setIdError('아이디는 특수문자로 끝날 수 없습니다')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (!idVerified) {
      setIdError('아이디 중복 확인을 해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    }

    if (!isSocialOnlyRegister) {
      const passwordErrorMsg = validatePassword(password)
      if (passwordErrorMsg) {
        setPasswordError(passwordErrorMsg)
        toastUtil.error('입력 값을 확인해주세요')
        return
      }

      if (!passwordConfirm) {
        setPasswordConfirmError('비밀번호를 다시 입력해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return
      } else if (password !== passwordConfirm) {
        setPasswordConfirmError('비밀번호가 일치하지 않습니다')
        toastUtil.error('입력 값을 확인해주세요')
        return
      }
    }

    if (!nickname) {
      setNicknameError('이름을 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (nickname.length < 2 || nickname.length > 15) {
      setNicknameError('이름은 2~15글자로 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    }

    if (!agreeTerms) {
      setAgreeTermsError('이용약관에 동의해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    }

    if (!agreePrivacy) {
      setAgreePrivacyError('개인정보 수집 및 이용에 동의해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    }

    if (skipPhoneVerification) {
      const digits = normalizeRegisterPhoneInput(phone)
      // 부트페이 없이 폼 입력 번호만 전송한다(형식 검사는 서버에 맡긴다).
      if (!digits) {
        setPhoneError('휴대폰 번호를 입력해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return
      }
      setPhoneError('')
    }

    setIsRegistering(true)

    try {
      const response = await verifyPhoneForRegister(
        {
          ...(socialProvider && socialProviderId && !useEmailPasswordWithSocial
            ? {}
            : { email }),
          username: id,
          ...(socialProvider && socialProviderId && !useEmailPasswordWithSocial
            ? {}
            : { password }),
          nickname,
          profile_image: profileImage,
          agree_terms: agreeTerms,
          agree_privacy: agreePrivacy,
          ...(socialProvider &&
            socialProviderId && {
              provider: socialProvider as SocialProvider,
              provider_id: socialProviderId,
            }),
        },
        skipPhoneVerification ? { debugPhone: phone } : undefined
      )

      // 회원가입 성공 시에만 처리
      if (response) {
        if (response.token) {
          setToken(response.token)
        }

        // 사용자 프로필 가져와서 ID 및 프로필 이미지 저장
        let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
        try {
          profileResponse = await userApi.getProfile()
          setUserId(profileResponse.user.id)
          setStoreProfileImage(profileResponse.user.profile_image || null)
        } catch (error) {
          // 프로필 로드 실패 시 무시
        }

        navigate(
          isApp()
            ? '/permissions'
            : getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
        )
      }
    } catch (error: any) {
      // 에러는 verifyPhoneForRegister 내부에서 처리됨
    } finally {
      setIsRegistering(false)
    }
  }

  const handleIdCheck = async () => {
    if (!id) {
      setIdError('아이디를 입력해주세요')
      return
    }

    if (idVerified) {
      toastUtil.info('이미 확인 완료된 아이디입니다')
      return
    }

    if (isCheckingId) {
      toastUtil.info('이미 확인 중입니다')
      return
    }

    setIsCheckingId(true)
    setIdError('')
    try {
      const response = await authApi.checkUsername({ username: id })
      if (response.available) {
        setIdVerified(true)
        setIdError('')
        toastUtil.success('사용 가능한 아이디입니다')
      } else {
        setIdVerified(false)
        setIdError(response.message || '사용할 수 없는 아이디입니다')
      }
    } catch (error: any) {
      setIdVerified(false)
      const apiError = error.response?.data as ApiErrorResponse
      if (apiError?.errors?.username) {
        setIdError(apiError.errors.username[0])
      } else {
        setIdError(apiError?.message || '아이디 확인 중 오류가 발생했습니다')
      }
    } finally {
      setIsCheckingId(false)
    }
  }

  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked)
    setAgreeTerms(checked)
    setAgreePrivacy(checked)
    if (checked) {
      setAgreeTermsError('')
      setAgreePrivacyError('')
    }
  }

  const handleProfileImageSelect = (file: File) => {
    setProfileImage(file)
    setProfileImageError('')
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const validateForm = (): boolean => {
    // 본인인증 전 폼 검증 (phone 제외)
    const isSocialOnlyRegister =
      !!(socialProvider && socialProviderId) && !useEmailPasswordWithSocial

    if (!isSocialOnlyRegister) {
      if (!email) {
        setEmailError('이메일을 입력해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailError('올바른 이메일 형식이 아닙니다')
        toastUtil.error('입력 값을 확인해주세요')
        return false
      } else if (!emailVerified) {
        setEmailError('이메일 중복 확인을 해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return false
      }
    }

    if (!id) {
      setIdError('아이디를 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    } else if (id.length < 3 || id.length > 20) {
      setIdError('아이디는 3~20자로 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    } else if (!/^[a-zA-Z0-9._]+$/.test(id)) {
      setIdError('아이디는 영문, 숫자, 언더스코어(_)·마침표(.)만 사용 가능합니다')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    } else if (/[._]$/.test(id)) {
      setIdError('아이디는 특수문자로 끝날 수 없습니다')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    } else if (!idVerified) {
      setIdError('아이디 중복 확인을 해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    }

    if (!isSocialOnlyRegister) {
      const passwordErrorMsg = validatePassword(password)
      if (passwordErrorMsg) {
        setPasswordError(passwordErrorMsg)
        toastUtil.error('입력 값을 확인해주세요')
        return false
      }

      if (!passwordConfirm) {
        setPasswordConfirmError('비밀번호를 다시 입력해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return false
      } else if (password !== passwordConfirm) {
        setPasswordConfirmError('비밀번호가 일치하지 않습니다')
        toastUtil.error('입력 값을 확인해주세요')
        return false
      }
    }

    if (!nickname) {
      setNicknameError('이름을 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    } else if (nickname.length < 2 || nickname.length > 15) {
      setNicknameError('이름은 2~15글자로 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    }

    if (!agreeTerms) {
      setAgreeTermsError('이용약관에 동의해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    }

    if (!agreePrivacy) {
      setAgreePrivacyError('개인정보 수집 및 이용에 동의해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return false
    }

    if (skipPhoneVerification) {
      const digits = normalizeRegisterPhoneInput(phone)
      if (!digits) {
        setPhoneError('휴대폰 번호를 입력해주세요')
        toastUtil.error('입력 값을 확인해주세요')
        return false
      }
      setPhoneError('')
    }

    return true
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()

    if (!validateForm()) {
      return
    }

    // 모든 검증 통과 시 본인인증 및 회원가입 진행
    await handleRegister()
  }

  return (
    <div className="w-full h-full flex flex-col">
      <AppBar title="회원가입" isBottomBorder />

      <div className="w-full flex-1 overflow-y-auto scrollbar-hide">
        <div className="app-container">
          <div className="flex flex-col px-20px pb-32px">
            <ProfileImageUpload
              profileImagePreview={profileImagePreview}
              error={profileImageError}
              onFileSelect={handleProfileImageSelect}
              onError={setProfileImageError}
            />

            <form onSubmit={handleSubmit} className="mt-16px">
              <FormInput
                label="아이디"
                name="id"
                value={id}
                onChange={(e) => {
                  setId(e.target.value)
                  setIdVerified(false)
                  setIdError('')
                }}
                placeholder="아이디를 입력해주세요"
                maxLength={20}
                buttonText={idVerified ? '확인완료' : '중복확인'}
                onButtonClick={handleIdCheck}
                buttonIsActive={!idVerified}
                error={idError}
              />

              {(!socialProvider ||
                !socialProviderId ||
                useEmailPasswordWithSocial) && (
                <FormInput
                  label="이메일"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const newEmail = e.target.value
                    setEmail(newEmail)
                    setEmailVerified(false)
                    if (
                      newEmail &&
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
                    ) {
                      setEmailError('올바른 이메일 형식이 아닙니다')
                    } else {
                      setEmailError('')
                    }
                  }}
                  placeholder="이메일을 입력해주세요"
                  buttonText={emailVerified ? '확인완료' : '중복확인'}
                  onButtonClick={handleEmailCheck}
                  buttonIsActive={!emailVerified}
                  error={emailError}
                />
              )}

              {(!socialProvider ||
                !socialProviderId ||
                useEmailPasswordWithSocial) && (
                <>
                  <FormInput
                    label="비밀번호"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      if (passwordError) {
                        setPasswordError('')
                      }
                      if (passwordConfirm && passwordConfirm !== e.target.value) {
                        setPasswordConfirmError('비밀번호가 일치하지 않습니다')
                      } else if (passwordConfirmError) {
                        setPasswordConfirmError('')
                      }
                    }}
                    placeholder="비밀번호를 입력해주세요"
                    showPasswordToggle
                    error={passwordError}
                  />

                  <FormInput
                    label="비밀번호 재입력"
                    name="passwordConfirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => {
                      setPasswordConfirm(e.target.value)
                      if (passwordConfirmError) {
                        setPasswordConfirmError('')
                      }
                      if (password && password !== e.target.value) {
                        setPasswordConfirmError('비밀번호가 일치하지 않습니다')
                      }
                    }}
                    placeholder="비밀번호를 다시 입력해주세요"
                    showPasswordToggle
                    error={passwordConfirmError}
                  />
                </>
              )}

              <FormInput
                label="이름"
                name="nickname"
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value)
                  if (nicknameError) {
                    setNicknameError('')
                  }
                }}
                placeholder="이름을 입력해주세요"
                error={nicknameError}
                maxLength={15}
              />

              {skipPhoneVerification && (
                <FormInput
                  label="휴대폰 번호"
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value)
                    if (phoneError) {
                      setPhoneError('')
                    }
                  }}
                  placeholder="01012345678"
                  subtitle="* 로컬 개발(development): 본인인증 없이 가입"
                  error={phoneError}
                />
              )}

              <div className="my-32px mb-24px">
                <div
                  className="flex items-center gap-8px mb-16px cursor-pointer"
                  onClick={() => handleAgreeAll(!agreeAll)}
                >
                  <BoldCheckbox checked={agreeAll} onChange={handleAgreeAll} />
                  <span className="flex-1 font-pretendard font-700 text-16px leading-24px text-brown-80%-main text-left">
                    모든 약관에 동의합니다.
                  </span>
                </div>

                <AgreementItem
                  checked={agreeTerms}
                  onChange={(checked) => {
                    setAgreeTerms(checked)
                    setAgreeAll(agreePrivacy && checked)
                    if (checked) {
                      setAgreeTermsError('')
                    }
                  }}
                  label="필수"
                  text="TASTEE 이용 약관"
                  onViewClick={() => setLegalViewer('terms')}
                />
                {agreeTermsError && (
                  <FormErrorMessage error={agreeTermsError} />
                )}

                <AgreementItem
                  checked={agreePrivacy}
                  onChange={(checked) => {
                    setAgreePrivacy(checked)
                    setAgreeAll(agreeTerms && checked)
                    if (checked) {
                      setAgreePrivacyError('')
                    }
                  }}
                  label="필수"
                  text="개인정보 수집 및 이용 안내"
                  onViewClick={() => setLegalViewer('privacy')}
                />
                {agreePrivacyError && (
                  <FormErrorMessage error={agreePrivacyError} />
                )}
              </div>

              <button
                type="submit"
                disabled={isRegistering}
                className={`
                  w-full
                  py-15px
                  rounded-30px
                  font-bold
                  text-16px
                  ${
                    isRegistering
                      ? 'cursor-not-allowed bg-brandPrimary-10% text-textColorText3'
                      : 'cursor-pointer bg-4B3621-80% text-textColorText1W'
                  }
                `}
              >
                {isRegistering
                    ? skipPhoneVerification
                    ? '가입 중...'
                    : '본인인증 중...'
                  : skipPhoneVerification
                  ? '회원가입'
                  : '본인인증'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* 약관 보기 — 앱 안 전체화면 뷰어(브라우저 이동 없음) */}
      <LegalDocViewer type={legalViewer} onClose={() => setLegalViewer(null)} />
    </div>
  )
}

export default Register
